import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT
  ? API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`
  : "/api";

const ALLOWED_MODULES = [4, 10];

const EMPTY_FORM = {
  Department: "",
  DepttAbb: "",
  Mobile: "",
  EMail: "",
  StateID: "",
  CityID: "",
  DepttFacultyID: "",
  Folder: "",
};

export default function DepartmentRecord() {
  const navigate = useNavigate();
  const localUser = JSON.parse(localStorage.getItem("user") || "null");
  const moduleId = parseInt(localUser?.moduleId, 10);

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(
        `${API_BASE}/guesthouse/departments-list?moduleId=${moduleId}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch departments");
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to fetch departments");
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) {
      navigate("/");
      return;
    }
    fetchDepartments();
  }, [moduleId, fetchDepartments, navigate]);

  // Fetch once to avoid double-consuming response body
  const loadDropdowns = async (row) => {
    try {
      const [sRes, cRes, fRes] = await Promise.all([
        fetch(`${API_BASE}/states`),
        fetch(`${API_BASE}/cities?stateId=${row.StateID}`),
        fetch(`${API_BASE}/infra/faculties`),
      ]);
      const [sData, cData, fData] = await Promise.all([
        sRes.json(), cRes.json(), fRes.json()
      ]);
      setStates(Array.isArray(sData) ? sData : []);
      setCities(Array.isArray(cData) ? cData : []);
      setFaculties(Array.isArray(fData) ? fData : []);
    } catch {
      // ignore
    }
  };

  const handleOpenEdit = async (row) => {
    setEditRow(row);
    setForm({
      Department: row.Department || "",
      DepttAbb: row.DepttAbb || "",
      Mobile: row.Mobile || "",
      EMail: row.EMail || "",
      StateID: row.StateID ? String(row.StateID) : "",
      CityID: row.CityID ? String(row.CityID) : "",
      DepttFacultyID: row.DepttFacultyID ? String(row.DepttFacultyID) : "",
      Folder: row.Folder || "",
    });
    setSaveError("");
    setShowModal(true);
    await loadDropdowns(row);
  };

  const handleStateChange = async (stateId) => {
    setForm((f) => ({ ...f, StateID: stateId, CityID: "" }));
    if (!stateId) { setCities([]); return; }
    try {
      const res = await fetch(`${API_BASE}/cities?stateId=${stateId}`);
      const data = await res.json();
      setCities(Array.isArray(data) ? data : []);
    } catch {
      setCities([]);
    }
  };

  const handleSave = async () => {
    if (!form.Department.trim()) {
      setSaveError("Department name is required.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const res = await fetch(
        `${API_BASE}/guesthouse/departments-list/${editRow.DepartmentID}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, moduleId }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update department");
      setShowModal(false);
      setSuccessMsg(data.message || "Updated successfully.");
      await fetchDepartments();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setSaveError(err.message || "Failed to update department");
    } finally {
      setSaving(false);
    }
  };

  if (!ALLOWED_MODULES.includes(moduleId)) return null;

  return (
    <section className="container-fluid">
      <h3 className="mb-3">Department Record</h3>

      {error && <div className="alert alert-danger">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover table-sm align-middle" style={{ '--bs-table-hover-bg': '#aed6f1' }}>
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>Department</th>
                <th>Mobile</th>
                <th>EMail</th>
                <th>Abb</th>
                <th>DepttID</th>
                <th>Faculty</th>
                <th>City</th>
                <th>State</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {departments.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center text-muted">
                    No departments found.
                  </td>
                </tr>
              ) : (
                departments.map((row, idx) => (
                  <tr key={row.DepartmentID}>
                    <td>{idx + 1}</td>
                    <td>{row.Department}</td>
                    <td>{row.Mobile}</td>
                    <td>{row.EMail}</td>
                    <td>{row.DepttAbb}</td>
                    <td>{row.DepartmentID}</td>
                    <td>{row.DepttFaculty}</td>
                    <td>{row.City}</td>
                    <td>{row.State}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleOpenEdit(row)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && editRow && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Department</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                {saveError && (
                  <div className="alert alert-danger">{saveError}</div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Department ID</label>
                  <input
                    className="form-control"
                    value={editRow.DepartmentID}
                    readOnly
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Department <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    value={form.Department}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, Department: e.target.value }))
                    }
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Abb</label>
                    <input
                      className="form-control"
                      value={form.DepttAbb}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, DepttAbb: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Mobile</label>
                    <input
                      className="form-control"
                      value={form.Mobile}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, Mobile: e.target.value }))
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">EMail</label>
                    <input
                      className="form-control"
                      type="email"
                      value={form.EMail}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, EMail: e.target.value }))
                      }
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">State</label>
                    <select
                      className="form-select"
                      value={form.StateID}
                      onChange={(e) => handleStateChange(e.target.value)}
                    >
                      <option value="">-- Select State --</option>
                      {states.map((s) => (
                        <option key={s.StateID} value={String(s.StateID)}>
                          {s.State}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">City</label>
                    <select
                      className="form-select"
                      value={form.CityID}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, CityID: e.target.value }))
                      }
                    >
                      <option value="">-- Select City --</option>
                      {cities.map((c) => (
                        <option key={c.CityID} value={String(c.CityID)}>
                          {c.City}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Faculty Of</label>
                    <select
                      className="form-select"
                      value={form.DepttFacultyID}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, DepttFacultyID: e.target.value }))
                      }
                    >
                      <option value="">-- Select Faculty --</option>
                      {faculties.map((fc) => (
                        <option
                          key={fc.DepttFacultyID}
                          value={String(fc.DepttFacultyID)}
                        >
                          {fc.DepttFaculty}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Folder</label>
                  <input
                    className="form-control"
                    value={form.Folder}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, Folder: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
